// Pure Classic -> Builder conversion. Vue-free, store-free, side-effect-free
// except a console.warn on uploadInput.json parse failure (Req 3.3, 6.3).
// Feature: classic-to-builder-place-sync

import { cloneDeepWith, isPlainObject } from "lodash";

// Maximum length of a derived Selected_Place name (Req 2.5).
const MAX_NAME_LENGTH = 255;

// Default name used when a feature has no usable properties.name (Req 2.6).
const DEFAULT_NAME = "Unnamed place";

/**
 * Derive the display name for a converted Classic feature (Req 2.5, 2.6):
 *   - properties.name, trimmed, truncated to at most 255 chars, when it is a
 *     non-empty, non-whitespace string.
 *   - else 'Unnamed place'.
 *
 * Never throws on null/undefined/garbage input.
 *
 * @param {Object} feature - A GeoJSON Feature.
 * @returns {string} The Selected_Place name.
 */
export function deriveName(feature) {
  const name = feature && feature.properties && feature.properties.name;
  if (typeof name === "string") {
    const trimmed = name.trim();
    if (trimmed.length > 0) {
      return trimmed.slice(0, MAX_NAME_LENGTH);
    }
  }
  return DEFAULT_NAME;
}

// Prefix for hash-derived ids, keeping them visually distinct and aligned with
// the _source: 'upload' classification consumed downstream (design).
const DERIVED_ID_PREFIX = "upload-";

// Property keys, in precedence order, that may carry an explicit stable id.
const EXPLICIT_PROPERTY_ID_KEYS = ["polygon_id", "_id", "id"];

/**
 * Deterministic, dependency-free 32-bit FNV-1a hash folded to an 8-char hex
 * string. Same input string always yields the same output; different strings
 * yield (with overwhelming probability) a different output.
 *
 * @param {string} str - The string to hash.
 * @returns {string} An 8-character lowercase hex hash.
 */
function fnv1aHex(str) {
  // FNV-1a 32-bit: offset basis 2166136261, prime 16777619.
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    // Multiply by the FNV prime using 32-bit overflow-safe math.
    hash = Math.imul(hash, 0x01000193);
  }
  // Coerce to an unsigned 32-bit int and pad to a fixed-width hex string.
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Recursively sort object keys so that logically-equal objects serialize to
 * the same canonical string. Arrays keep their order (coordinate order is
 * semantically significant); plain objects have their keys sorted.
 *
 * @param {*} value - Any JSON-serializable value.
 * @returns {*} A structurally-equal value with plain-object keys sorted.
 */
function sortKeysDeep(value) {
  return cloneDeepWith(value, (val) => {
    if (isPlainObject(val)) {
      return Object.keys(val)
        .sort()
        .reduce((acc, key) => {
          acc[key] = sortKeysDeep(val[key]);
          return acc;
        }, {});
    }
    // Returning undefined defers cloning of arrays/primitives to lodash.
    return undefined;
  });
}

/**
 * Build a canonical, key-sorted JSON string of { geometry, properties } so that
 * two features with equal geometry and properties always produce the same
 * string regardless of original key ordering.
 *
 * @param {Object} feature - A GeoJSON Feature.
 * @returns {string} The canonical string.
 */
function canonicalString(feature) {
  const geometry = feature && feature.geometry;
  const properties = (feature && feature.properties) || null;
  return JSON.stringify({
    geometry: sortKeysDeep(geometry),
    properties: sortKeysDeep(properties),
  });
}

/**
 * Extract an explicit, non-empty stable id from a feature, if present.
 * Precedence: feature.id, then properties.polygon_id / _id / id. A value is
 * usable when, once coerced to a trimmed string, it is non-empty.
 *
 * @param {Object} feature - A GeoJSON Feature.
 * @returns {string|null} The trimmed explicit id, or null when none.
 */
function explicitId(feature) {
  if (!feature) {
    return null;
  }
  const candidates = [feature.id];
  const properties = feature.properties;
  if (properties) {
    EXPLICIT_PROPERTY_ID_KEYS.forEach((key) => candidates.push(properties[key]));
  }
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate === null || candidate === undefined) {
      continue;
    }
    const trimmed = String(candidate).trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return null;
}

/**
 * Derive a STABLE _id for a converted Classic feature (Req 2.8, 4.4, 5.1-5.3).
 *
 * Precedence:
 *   1. A non-empty feature.id or feature.properties.polygon_id / _id / id
 *      (trimmed, coerced to string) is used verbatim, so re-cloned jobs keep
 *      their identity.
 *   2. Otherwise a deterministic hash of a canonical, key-sorted stringify of
 *      { geometry, properties }, prefixed with 'upload-'. Identical
 *      geometry+properties always yields the same _id (Req 5.1, 5.2); different
 *      data yields (with overwhelming probability) a different _id (Req 5.3).
 *   3. Returns '' when no geometry is present and no explicit id is available,
 *      so callers exclude the place (Req 4.4).
 *
 * Never throws.
 *
 * @param {Object} feature - A GeoJSON Feature.
 * @returns {string} A stable, non-empty _id, or '' when none derivable.
 */
export function deriveStableId(feature) {
  try {
    const explicit = explicitId(feature);
    if (explicit) {
      return explicit;
    }
    // No explicit id: fall back to hashing geometry + properties. Without a
    // geometry there is nothing stable to hash, so the place is excluded.
    if (!feature || feature.geometry === null || feature.geometry === undefined) {
      return "";
    }
    return `${DERIVED_ID_PREFIX}${fnv1aHex(canonicalString(feature))}`;
  } catch (e) {
    // Defense in depth: deriveStableId must never throw (Req 6.1).
    return "";
  }
}

/**
 * Normalize an arbitrary GeoJSON-ish value into an array of Features.
 *
 * Accepts a FeatureCollection, a single Feature, a bare geometry, or an array
 * of any of those, and returns a flat array of Feature-shaped objects. A bare
 * geometry (one carrying a `type`/`coordinates` but no `geometry`) is wrapped
 * as `{ type: 'Feature', geometry: <value>, properties: null }`. Values that
 * are not object-like are dropped. Never throws.
 *
 * @param {*} value - A FeatureCollection, Feature, geometry, or array thereof.
 * @returns {Array<Object>} Flat array of Feature-shaped objects.
 */
function toFeatureArray(value) {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => acc.concat(toFeatureArray(item)), []);
  }
  if (typeof value !== "object") {
    return [];
  }
  // A FeatureCollection: recurse into its features array.
  if (value.type === "FeatureCollection" || Array.isArray(value.features)) {
    return toFeatureArray(value.features || []);
  }
  // Already a Feature (has a geometry key, even if null).
  if (
    value.type === "Feature" ||
    Object.prototype.hasOwnProperty.call(value, "geometry")
  ) {
    return [value];
  }
  // A bare geometry object: wrap it as a Feature.
  if (Object.prototype.hasOwnProperty.call(value, "coordinates")) {
    return [{ type: "Feature", geometry: value, properties: null }];
  }
  return [];
}

/**
 * Count how many Features a FeatureCollection-like value contributes.
 *
 * @param {*} value - A FeatureCollection, Feature, geometry, or array thereof.
 * @returns {number} The number of Features.
 */
function featureCount(value) {
  return toFeatureArray(value).length;
}

/**
 * Build the cross-source de-duplication key for a Feature: a canonical
 * key-sorted stringify of { geometry, properties }, so two Features with equal
 * geometry coordinates and equal properties collapse to one (Req 3.4). Reuses
 * the same canonical serialization as deriveStableId's hash input.
 *
 * @param {Object} feature - A Feature-shaped object.
 * @returns {string} The de-duplication key.
 */
function dedupeKey(feature) {
  try {
    return canonicalString(feature);
  } catch (e) {
    // A feature we cannot serialize is treated as unique (best effort).
    return `__unkeyable-${Math.random()}`;
  }
}

/**
 * Read Classic polygon data as an ordered list of GeoJSON Features, in source
 * priority order, de-duplicated across sources by geometry + properties
 * equality while preserving first-seen order (Req 3.1-3.5, 6.3).
 *
 * Priority:
 *   1. jobRequest.polygonInputOptions.geojson (FeatureCollection) when it has
 *      >= 1 Feature (Req 3.1).
 *   2. else parse jobRequest.uploadInput.json (stringified FeatureCollection)
 *      inside try/catch; on parse failure console.warn and contribute zero
 *      features (Req 3.2, 3.3, 6.3).
 *   3. jobRequest.selectedPolygons (array of Features/geometries) - merged in.
 *   4. jobRequest.drawnPolygonGeoJSON (Feature or FeatureCollection) - merged.
 *
 * Never throws on null/undefined/garbage jobRequest; always returns an array.
 *
 * @param {Object} jobRequest - The session jobRequest state slice.
 * @returns {Array<Object>} Ordered, cross-source de-duplicated Features.
 */
export function readClassicFeatures(jobRequest) {
  const collected = [];
  if (!jobRequest || typeof jobRequest !== "object") {
    return collected;
  }

  // Source 1: polygonInputOptions.geojson, when it has at least one Feature.
  const geojson =
    jobRequest.polygonInputOptions && jobRequest.polygonInputOptions.geojson;
  if (featureCount(geojson) >= 1) {
    collected.push(...toFeatureArray(geojson));
  } else {
    // Source 2 (only when Source 1 is absent/empty): parse uploadInput.json.
    const rawJson = jobRequest.uploadInput && jobRequest.uploadInput.json;
    if (typeof rawJson === "string" && rawJson.trim().length > 0) {
      try {
        const parsed = JSON.parse(rawJson);
        collected.push(...toFeatureArray(parsed));
      } catch (e) {
        // Parse failure: contribute zero features and log (Req 3.3, 6.3).
        // eslint-disable-next-line no-console
        console.warn(
          "[classicToBuilderSync] failed to parse uploadInput.json",
          e
        );
      }
    }
  }

  // Source 3: selectedPolygons (array of Features/geometries) - always merged.
  collected.push(...toFeatureArray(jobRequest.selectedPolygons));

  // Source 4: drawnPolygonGeoJSON (Feature or FeatureCollection) - merged.
  collected.push(...toFeatureArray(jobRequest.drawnPolygonGeoJSON));

  // Cross-source de-duplicate by geometry + properties equality, preserving
  // first-seen order (Req 3.4).
  const seen = new Set();
  const deduped = [];
  collected.forEach((feature) => {
    const key = dedupeKey(feature);
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(feature);
    }
  });
  return deduped;
}

// Geometry types that convert into a Selected_Place (Req 2.3, 2.4, 6.4).
const SUPPORTED_GEOMETRY_TYPES = ["Polygon", "MultiPolygon", "Point"];

/**
 * Whether a feature carries a geometry of a supported type (Req 2.3, 2.4, 6.4).
 *
 * @param {Object} feature - A Feature-shaped object.
 * @returns {boolean} True when geometry is Polygon/MultiPolygon/Point.
 */
function hasSupportedGeometry(feature) {
  const geometry = feature && feature.geometry;
  return (
    !!geometry &&
    typeof geometry === "object" &&
    SUPPORTED_GEOMETRY_TYPES.indexOf(geometry.type) !== -1
  );
}

/**
 * Convert Classic polygon data into an ordered list of ConvertedPlace objects
 * ready to hand to the addSelectedPlaces mutation (Req 2.1-2.4, 2.7, 2.9, 4.3,
 * 4.4, 6.1, 6.2, 6.4, 6.5).
 *
 * For each Feature read via readClassicFeatures:
 *   - Keep only Polygon / MultiPolygon / Point geometries; skip absent, null,
 *     or other geometry types (Req 2.4, 6.4).
 *   - geometry is copied through unchanged (Req 2.3).
 *   - name := deriveName(feature) (Req 2.5, 2.6).
 *   - _source := 'upload' (Req 2.7).
 *   - _id := deriveStableId(feature) (Req 2.8).
 * Features whose derived _id is '' are excluded (Req 4.4). The result is
 * de-duplicated by _id, keeping the first occurrence (Req 2.9, 4.3).
 *
 * Never throws; always returns an array (empty when nothing convertible)
 * (Req 2.2, 6.1, 6.2, 6.5).
 *
 * @param {Object} jobRequest - The session jobRequest state slice.
 * @returns {Array<Object>} Ordered, _id-unique converted places.
 */
export function convertClassicPolygons(jobRequest) {
  try {
    const features = readClassicFeatures(jobRequest);
    const seen = new Set();
    const converted = [];
    features.forEach((feature) => {
      // Skip features without a supported geometry type (Req 2.4, 6.4).
      if (!hasSupportedGeometry(feature)) {
        return;
      }
      const id = deriveStableId(feature);
      // Exclude empty-_id places and de-dupe by _id keeping the first
      // occurrence (Req 2.9, 4.3, 4.4).
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      converted.push({
        _id: id,
        _source: "upload",
        name: deriveName(feature),
        geometry: feature.geometry,
      });
    });
    return converted;
  } catch (e) {
    // Defense in depth: convertClassicPolygons must never throw (Req 6.1).
    // eslint-disable-next-line no-console
    console.warn("[classicToBuilderSync] conversion failed", e);
    return [];
  }
}
